import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { type BasePageResponse, type BaseResponse } from '../models'
import { type PageRequest } from '../../models/PageRequest'
import {
  type CreatedExternalDataSource,
  type ExternalDataSource,
  type ExternalDataSourceCreateBody,
  type ExternalDataSourceUpdateBody,
  type TestFetchResult,
  type TestFetchSubject,
  type VerifyResult,
} from '../../models/ExternalDataSource'

const _BASE_URL = import.meta.env.REACT_APP_INTEGRATIONS_SERVICE_API_BASE_URL!

interface AppScopedRequest {
  accountId: string
  clientId: string
}

interface DataSourceRequest extends AppScopedRequest {
  dataSourceId: string
}

export const externalDataSourcesApi = createApi({
  reducerPath: 'externalDataSourcesApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['app-data-sources'],
  endpoints: (builder) => ({
    listAppDataSources: builder.query<BasePageResponse<ExternalDataSource[]>, AppScopedRequest & { pageRequest?: PageRequest }>({
      query: ({ accountId, clientId, pageRequest }) => ({
        url: `/accounts/${accountId}/apps/${clientId}/external-data-sources/`,
        method: 'GET',
        params: pageRequest ? { page: pageRequest.page, size: pageRequest.size } : undefined,
      }),
      providesTags: (_result, _error, { clientId }) => [{ type: 'app-data-sources', id: clientId }],
    }),
    createDataSource: builder.mutation<BaseResponse<CreatedExternalDataSource>, AppScopedRequest & { payload: ExternalDataSourceCreateBody }>({
      query: ({ accountId, clientId, payload }) => ({
        url: `/accounts/${accountId}/apps/${clientId}/external-data-sources/`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_result, _error, { clientId }) => [{ type: 'app-data-sources', id: clientId }],
    }),
    updateDataSource: builder.mutation<BaseResponse<ExternalDataSource>, DataSourceRequest & { payload: ExternalDataSourceUpdateBody }>({
      query: ({ accountId, clientId, dataSourceId, payload }) => ({
        url: `/accounts/${accountId}/apps/${clientId}/external-data-sources/${dataSourceId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (_result, _error, { clientId }) => [{ type: 'app-data-sources', id: clientId }],
    }),
    deleteDataSource: builder.mutation<void, DataSourceRequest>({
      query: ({ accountId, clientId, dataSourceId }) => ({
        url: `/accounts/${accountId}/apps/${clientId}/external-data-sources/${dataSourceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { clientId }) => [{ type: 'app-data-sources', id: clientId }],
    }),
    verifyDataSource: builder.mutation<BaseResponse<VerifyResult>, DataSourceRequest>({
      query: ({ accountId, clientId, dataSourceId }) => ({
        url: `/accounts/${accountId}/apps/${clientId}/external-data-sources/${dataSourceId}/verify`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { clientId }) => [{ type: 'app-data-sources', id: clientId }],
    }),
    testDataSource: builder.mutation<BaseResponse<TestFetchResult>, DataSourceRequest & { subject: TestFetchSubject; dataTypes?: string[] }>({
      query: ({ accountId, clientId, dataSourceId, subject, dataTypes }) => ({
        url: `/accounts/${accountId}/apps/${clientId}/external-data-sources/${dataSourceId}/test`,
        method: 'POST',
        body: dataTypes ? { subject, dataTypes } : { subject },
      }),
      invalidatesTags: (_result, _error, { clientId }) => [{ type: 'app-data-sources', id: clientId }],
    }),
  }),
})

export const {
  useListAppDataSourcesQuery,
  useCreateDataSourceMutation,
  useUpdateDataSourceMutation,
  useDeleteDataSourceMutation,
  useVerifyDataSourceMutation,
  useTestDataSourceMutation,
} = externalDataSourcesApi
